import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Pencil, Plus, Trash2 } from "lucide-react";
import * as React from "react";

import { DomainDialog } from "@/components/authoring/domain-dialog";
import { PageHeader } from "@/components/layout-bits";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import {
  Badge,
  Button,
  Card,
  Skeleton,
  TBody,
  TD,
  TH,
  THead,
  TR,
  Table,
} from "@/components/ui/primitives";
import { EmptyState, ErrorState } from "@/components/ui/states";
import { toast } from "@/components/ui/toaster";
import { deleteDomain } from "@/lib/api/endpoints";
import { useMyDomains } from "@/lib/query/hooks";
import { qk } from "@/lib/query/keys";
import type { DomainView } from "@/types/api";

export default function DomainsPage() {
  const { data, isLoading, isError, error, refetch } = useMyDomains();
  const qc = useQueryClient();

  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [editing, setEditing] = React.useState<DomainView | undefined>();
  const [toDelete, setToDelete] = React.useState<DomainView | undefined>();

  const del = useMutation({
    mutationFn: (slug: string) => deleteDomain(slug),
    onSuccess: async () => {
      toast.success("Domain deleted.");
      await qc.invalidateQueries({ queryKey: qk.myDomains });
      setToDelete(undefined);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const openCreate = () => {
    setEditing(undefined);
    setDialogOpen(true);
  };
  const openEdit = (d: DomainView) => {
    setEditing(d);
    setDialogOpen(true);
  };

  return (
    <div>
      <PageHeader
        title="Domains"
        description="Group your agents and capabilities into domains."
        actions={
          <Button onClick={openCreate}>
            <Plus className="h-4 w-4" />
            New domain
          </Button>
        }
      />

      {isError ? (
        <ErrorState error={error} onRetry={() => refetch()} />
      ) : isLoading ? (
        <Skeleton className="h-40 w-full" />
      ) : (data ?? []).length === 0 ? (
        <EmptyState
          title="No domains yet"
          hint="Create your first domain to start adding agents."
        />
      ) : (
        <Card className="p-4">
          <Table>
            <THead>
              <TR>
                <TH>Name</TH>
                <TH>Slug</TH>
                <TH>Agents</TH>
                <TH>Access</TH>
                <TH className="w-24 text-right">Actions</TH>
              </TR>
            </THead>
            <TBody>
              {(data ?? []).map((d) => (
                <TR key={d.id}>
                  <TD className="font-medium">{d.name}</TD>
                  <TD className="font-mono text-xs text-muted-foreground">{d.slug}</TD>
                  <TD>
                    <Badge variant="secondary">{d.agent_count}</Badge>
                  </TD>
                  <TD>
                    {d.allowed_issuers.length > 0 || d.allowed_audiences.length > 0 ? (
                      <Badge variant="secondary">Restricted</Badge>
                    ) : (
                      <span className="text-xs text-muted-foreground">Any caller</span>
                    )}
                  </TD>
                  <TD className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label="Edit"
                        onClick={() => openEdit(d)}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label="Delete"
                        onClick={() => setToDelete(d)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        </Card>
      )}

      <DomainDialog open={dialogOpen} onOpenChange={setDialogOpen} domain={editing} />
      <ConfirmDialog
        open={!!toDelete}
        onOpenChange={(o) => !o && setToDelete(undefined)}
        title={`Delete domain “${toDelete?.name}”?`}
        description="This cannot be undone. Domains with agents or capabilities can't be deleted."
        pending={del.isPending}
        onConfirm={() => toDelete && del.mutate(toDelete.slug)}
      />
    </div>
  );
}
